import { COLS_BAR_HEIGHT, ROWS_BAR_WIDTH } from './consts';
import {
  $tempSelection,
  $tempUnion,
  addTempSelection,
  clearSelections,
  setTempEnd,
  useRowSelected,
  useSelectionInRow,
} from './selection';
import { RectRange } from '@/model/range';
import type { Sheet } from '@/model/sheet';
import type { Unit } from '@/model/types';
import { undoManager } from '@/undo';
import { Vector2 } from '@/utils/vec';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { atom } from 'nanostores';
import { type ReactNode, useCallback } from 'react';

export const $dragSelecting = atom(false);

function RowView({
  posY,
  rowIndex,
  onSetRowHeight,
  sheet,
}: {
  posY: number;
  rowIndex: number;
  onSetRowHeight: (rowIndex: number, height: Unit) => void;
  sheet: Sheet;
}) {
  const dragging = useStore($dragSelecting);
  const selected = useRowSelected(rowIndex);
  const inSelection = useSelectionInRow(rowIndex);
  const height = sheet.getRowHeight(rowIndex);

  function onHandleMouseDown(e: React.MouseEvent) {
    e.stopPropagation();
    const startY = e.clientY;
    const startHeight = height;

    const tx = undoManager.startTransaction(() =>
      onSetRowHeight(rowIndex, startHeight),
    );

    function onMouseMove(e: MouseEvent): number {
      const dy = e.clientY - startY;
      const newHeight = Math.max(startHeight + dy, 0);
      onSetRowHeight(rowIndex, newHeight);
      return newHeight;
    }

    function onMouseUp(e: MouseEvent) {
      const newHeight = onMouseMove(e);
      if (newHeight !== startHeight) {
        tx.commit(
          () => onSetRowHeight(rowIndex, newHeight),
          `Resize row ${rowIndex + 1}`,
        );
      } else {
        tx.cancel();
      }

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  function onDoubleClick() {
    onSetRowHeight(rowIndex, 'auto');
    undoManager.push({
      undo: () => onSetRowHeight(rowIndex, height),
      redo: () => onSetRowHeight(rowIndex, 'auto'),
      description: `Auto resize row ${rowIndex + 1}`,
    });
  }

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!e.shiftKey) {
        clearSelections();
      }
      $tempSelection.set(
        RectRange.rows(new Vector2(-Infinity, rowIndex), rowIndex),
      );
      $tempUnion.set(!selected || !e.shiftKey);
      $dragSelecting.set(true);

      function onMouseUp() {
        $dragSelecting.set(false);
        addTempSelection();
        window.removeEventListener('mouseup', onMouseUp);
      }
      window.addEventListener('mouseup', onMouseUp);
    },
    [rowIndex, selected],
  );

  const onMouseMove = useCallback(() => {
    if (!$dragSelecting.get()) return;
    setTempEnd(new Vector2(Infinity, rowIndex));
  }, [rowIndex]);

  if (height === 0) {
    return (
      <div
        className="group absolute left-0 z-2 flex -translate-y-1/2 items-center justify-center border-t border-r border-b border-black bg-white"
        style={{
          top: posY,
          height: 4,
          width: ROWS_BAR_WIDTH,
        }}
      >
        <div
          className="pointer-events-auto absolute top-0 left-full hidden h-8 w-2 -translate-y-1/2 bg-black/50 group-hover:block"
          onClick={onDoubleClick}
        ></div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'absolute left-0 flex w-16 items-center justify-end border-t border-r border-b-black/50 bg-gray-100 pr-1 select-none',
        selected && 'bg-gray-200',
        selected && 'bg-red-800 font-bold text-white',
        inSelection && !selected && 'bg-gray-200',
      )}
      style={{
        top: posY,
        height: sheet.getRowHeight(rowIndex),
        width: ROWS_BAR_WIDTH,
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
    >
      {dragging && (
        <div
          className="absolute top-0 right-[-100vw] bottom-0 left-[-100vw] z-1"
          onMouseMove={onMouseMove}
        ></div>
      )}
      {rowIndex + 1}
      <div
        className={clsx(
          'absolute right-0 bottom-0 left-0 z-1 h-2 translate-y-1/2 cursor-ns-resize',
          'after:absolute after:right-0 after:bottom-1/2 after:left-0 after:h-0 after:translate-y-1/2 after:bg-black/50 after:transition-all hover:after:h-full',
        )}
        onMouseDown={onHandleMouseDown}
        onDoubleClick={onDoubleClick}
      ></div>
    </div>
  );
}

export function RowsView({
  posY,
  startRow,
  endRow,
  onSetRowHeight,
  sheet,
}: {
  posY: number;
  startRow: number;
  endRow: number;
  onSetRowHeight: (rowIndex: number, height: Unit) => void;
  sheet: Sheet;
}) {
  const rows: ReactNode[] = [];

  let currPosY = posY;

  for (let i = startRow; i <= endRow; i++) {
    rows.push(
      <RowView
        key={i}
        posY={currPosY}
        rowIndex={i}
        onSetRowHeight={onSetRowHeight}
        sheet={sheet}
      />,
    );
    currPosY += sheet.getRowHeight(i);
  }

  return <div className="sticky left-0">{rows}</div>;
}
